import { useState, useEffect, useRef } from 'react';
import { Monitor, Terminal, Cpu, Copy, Check, RefreshCw, ChevronRight, ExternalLink } from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';
import { useBranding } from '../../../contexts/BrandingContext';

const PLATFORMS = [
  { key: 'windows', label: 'Windows', icon: Monitor },
  { key: 'linux', label: 'Linux', icon: Terminal },
  { key: 'arm', label: 'Raspberry Pi', icon: Cpu },
];

const AgentSetupStep = ({ onNext }) => {
  const { markStepComplete, prerequisites } = useSiteSetupStore();
  const { branding } = useBranding();
  const brandName = branding.brandName;
  // "Serverli Agent" is the product name. On white-labeled instances, drop the prefix.
  const agentLabel = branding.isWhiteLabeled ? 'Agent' : 'Serverli Agent';
  const [platform, setPlatform] = useState('windows');
  const [pairingCode, setPairingCode] = useState(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [agentConnected, setAgentConnected] = useState(false);
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  // On mount: check for existing agents first, only generate code if needed
  useEffect(() => {
    checkExistingAgents();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const checkExistingAgents = async () => {
    try {
      const res = await fetchWithTenant('/api/md/agents');
      if (res.ok) {
        const data = await res.json();
        const agents = data.agents || data || [];
        const online = agents.find(a => a.status === 'CONNECTED' || a.status === 'ONLINE');
        if (online) {
          setAgentConnected(true);
          return; // Agent already exists and is connected, no need to pair
        }
      }
    } catch (err) {
      // Fall through to generate pairing code
    }
    // No connected agent found, generate pairing code
    generatePairingCode();
  };

  // If agent already exists, skip ahead
  useEffect(() => {
    if (prerequisites?.hasConnectedAgent) {
      setAgentConnected(true);
    }
  }, [prerequisites]);

  // Poll for agent connection (always poll, not just when pairing code exists)
  useEffect(() => {
    if (!agentConnected) {
      checkAgentStatus(); // Check immediately
      pollRef.current = setInterval(checkAgentStatus, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [agentConnected]);

  const [pairingError, setPairingError] = useState(null);

  const generatePairingCode = async () => {
    setLoadingCode(true);
    setPairingError(null);
    try {
      const res = await fetchWithTenant('/api/md/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Site Agent', description: 'Created during site setup wizard' })
      });
      if (res.ok) {
        const data = await res.json();
        setPairingCode(data.pairingCode || data.code);
      } else {
        const errData = await res.json().catch(() => ({}));
        console.error('[AgentSetup] Pairing error:', errData);
        setPairingError(errData.error || 'Failed to generate pairing code. Try again.');
      }
    } catch (err) {
      console.error('[AgentSetup] Failed to generate pairing code:', err);
      setPairingError('Network error. Check your connection.');
    } finally {
      setLoadingCode(false);
    }
  };

  const checkAgentStatus = async () => {
    try {
      setChecking(true);
      const res = await fetchWithTenant('/api/md/agents');
      if (res.ok) {
        const data = await res.json();
        const agents = data.agents || data || [];
        const connected = agents.find(a => a.status === 'CONNECTED');
        if (connected) {
          setAgentConnected(true);
          if (pollRef.current) clearInterval(pollRef.current);
        }
      }
    } catch (err) {
      // Silently retry
    } finally {
      setChecking(false);
    }
  };

  const copyCode = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleContinue = async () => {
    await markStepComplete('agent_setup', { platform });
    onNext();
  };

  const renderInstructions = () => {
    const baseUrl = window.location.origin;

    if (platform === 'windows') {
      return (
        <div className="agent-instructions">
          <div className="instruction-step">
            <span className="instruction-number">1</span>
            <div>
              <strong>Download the Agent</strong>
              <p>Download the Windows installer from your CCM server.</p>
              
              <a
                href="/api/md/agent/download/windows-amd64"
                className="site-setup-btn outline small"
                target="_blank"
              >
                <Monitor size={14} /> Download for Windows <ExternalLink size={12} />
              </a>
            </div>
          </div>
          <div className="instruction-step">
            <span className="instruction-number">2</span>
            <div>
              <strong>Run as Administrator</strong>
              <p>Right-click the installer and select "Run as Administrator". Follow the prompts.</p>
            </div>
          </div>
          <div className="instruction-step">
            <span className="instruction-number">3</span>
            <div>
              <strong>Enter the Pairing Code</strong>
              <p>When prompted, enter the code shown below. The agent will connect to your CCM server at <code>{baseUrl}</code>.</p>
            </div>
          </div>
        </div>
      );
    }

    if (platform === 'linux' || platform === 'arm') {
      return (
        <div className="agent-instructions">
          <div className="instruction-step">
            <span className="instruction-number">1</span>
            <div>
              <strong>Download the Agent</strong>
              <p>Download the {platform === 'arm' ? 'ARM' : 'Linux'} binary to your server:</p>
              <code className="code-block">
                curl -o serverlimd-agent {baseUrl}/api/md/agent/download/{platform === 'arm' ? 'linux-arm64' : 'linux-amd64'}
              </code>
            </div>
          </div>
          <div className="instruction-step">
            <span className="instruction-number">2</span>
            <div>
              <strong>Make Executable and Run</strong>
              <code className="code-block">
                chmod +x serverlimd-agent{'\n'}sudo ./serverlimd-agent --server {baseUrl} --pair-code {pairingCode || 'XXXXXX'}
              </code>
            </div>
          </div>
          <div className="instruction-step">
            <span className="instruction-number">3</span>
            <div>
              <strong>Optional: Install as Service</strong>
              <p>For the agent to start automatically on boot:</p>
              <code className="code-block">
                sudo ./serverlimd-agent --install-service
              </code>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="site-setup-step agent-setup-step">
      <h3>Install the {agentLabel}</h3>
      <p className="step-description">
        The agent runs on your local network and connects your devices to {brandName}.
        Choose your platform and follow the instructions.
      </p>

      {/* Platform Tabs */}
      <div className="platform-tabs">
        {PLATFORMS.map(p => (
          <button
            key={p.key}
            className={'platform-tab' + (platform === p.key ? ' active' : '')}
            onClick={() => setPlatform(p.key)}
          >
            <p.icon size={16} />
            <span>{p.label}</span>
          </button>
        ))}
      </div>

      {/* Instructions */}
      {renderInstructions()}

      {/* Pairing Code */}
      <div className="pairing-code-section">
        <label>Your Pairing Code</label>
        {loadingCode ? (
          <div className="pairing-code-loading">
            <RefreshCw size={16} className="spin" /> Generating...
          </div>
        ) : pairingError ? (
          <div className="pairing-code-error">
            <span>{pairingError}</span>
            <button className="site-setup-btn outline small" onClick={generatePairingCode}>Retry</button>
          </div>
        ) : (
          <div className="pairing-code-display">
            <span className="pairing-code">{pairingCode || '------'}</span>
            <button className="site-setup-btn outline small" onClick={copyCode}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>

      {/* Connection Status */}
      <div className={'agent-status-card' + (agentConnected ? ' connected' : '')}>
        {agentConnected ? (
          <>
            <Check size={20} />
            <div>
              <strong>Agent Connected</strong>
              <span>Your agent is paired and communicating with {brandName}.</span>
            </div>
          </>
        ) : (
          <>
            <RefreshCw size={20} className="spin" />
            <div>
              <strong>Waiting for agent...</strong>
              <span>Install the agent and enter the pairing code. This page will update automatically.</span>
            </div>
          </>
        )}
      </div>

      {/* Continue */}
      <div className="step-actions">
        <button
          className="site-setup-btn primary"
          onClick={handleContinue}
          disabled={!agentConnected}
        >
          Continue <ChevronRight size={16} />
        </button>
        {!agentConnected && (
          <span className="step-hint">Agent must be connected to continue</span>
        )}
      </div>
    </div>
  );
};

export default AgentSetupStep;
